/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { When } from "react-if";
import { Box, Heading, FlexContainer, Text, theme } from "@nordcloud/gnui";
import { Application } from "~/generated/graphql";
import { ExpandIcon } from "~/components";
import { TableWrapper } from "~/components/Tables";
import { useToggle } from "~/hooks";
import { inflect } from "~/tools";
import { ApplicationEnvironment } from "../types";
import { Actions } from "./Actions";
import { applicationEnvironmentsColumns } from "./ApplicationEnvironmentsColumns";

type Props = {
  application: Application;
};

export function ApplicationItem({ application }: Props) {
  const [isExpanded, toggleIsExpanded] = useToggle();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const totalEnvironments = application.environments?.length ?? 0;
  const totalResources =
    application.environments?.reduce(
      (total, environment) => total + environment.resourceCount,
      0
    ) ?? 0;

  const data =
    application.environments?.map((environment) => ({
      name: environment.displayName ?? environment.name,
      resourcesCount: environment.resourceCount,
    })) ?? [];

  const applicationName = application.displayName ?? application.name;

  return (
    <Box spacing="spacing02" boxStyle="lightGrey">
      <FlexContainer justifyContent="space-between">
        <FlexContainer alignItems="center">
          <FlexContainer justifyContent="center" alignItems="center">
            <ExpandIcon
              name="chevronDown"
              title="chevronDown"
              isExpanded={isExpanded}
              onClick={toggleIsExpanded}
            />
          </FlexContainer>
          <div>
            <Heading css={{ wordBreak: "break-all" }} level={4}>
              {applicationName}
            </Heading>
            <Text size="sm" marginBottom={0} color={theme.color.text.text02}>
              {totalEnvironments} {inflect("Environment")(totalEnvironments)} |{" "}
              {totalResources} {inflect("Resource")(totalResources)}
            </Text>
          </div>
        </FlexContainer>
        <FlexContainer justifyContent="space-around">
          <Actions application={application} />
        </FlexContainer>
      </FlexContainer>
      <When condition={isExpanded}>
        <Heading padding={"0.75rem"} level={4}>
          Application Environments
        </Heading>
        <TableWrapper<ApplicationEnvironment>
          loading={false}
          data={data}
          columns={applicationEnvironmentsColumns()}
          rowSelection={rowSelection}
          selectedRowsHandler={() => null}
          setRowSelection={setRowSelection}
          stripped
        />
      </When>
    </Box>
  );
}
